import {
  AudienceFragment,
  ConstraintFragment,
  OfferFragment,
  ProductGroupFragment,
  Earning_Mechanism_Product_Counting_Attribute,
  PaymentType,
} from '@occc/graphql';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';

import {
  getEligibleProductGroupsByIds,
  getStoreLocationGroupConstraint,
} from '@occc/feat-constraints';
import { pluralize } from '../../../lib/strings';

// TODO: think how improve
const prepareConstraints = (
  constraints: ConstraintFragment[],
  productGroups: ProductGroupFragment[] | undefined,
  audience?: AudienceFragment,
) => {
  const inAudienceConstraint = constraints.find(constraintHelper.isInAudience);
  const daysOfWeek = constraints.find(constraintHelper.isDaysOfWeek);
  const timeOfDay = constraints.find(constraintHelper.isTimeOfDay);
  const minimumPurchaseConstraints = constraints
    .filter(constraintHelper.isPurchaseThreshold)
    .map((constraint) => {
      const eligibleProductGroups = getEligibleProductGroupsByIds(
        constraint.eligibleProductGroupIds,
        productGroups,
      );

      return {
        ...constraint,
        eligibleProductGroups,
      };
    });

  return {
    requiresActivation: constraints.find(constraintHelper.isRequiresActivation),
    locationInState: constraints.find(constraintHelper.isLocationInState),
    inAudience: inAudienceConstraint && audience ? audience : undefined,
    storeLocation: constraints.find(constraintHelper.isStoreLocation),
    storeLocationGroup: getStoreLocationGroupConstraint(constraints),
    subBrand: constraints.find(constraintHelper.isSubBrand),
    requiresVoucher: !!constraints.find(constraintHelper.isRequiresVoucher),
    emailRequired: constraints.find(constraintHelper.isEmailRequired),
    fulfillmentWindow:
      daysOfWeek || timeOfDay
        ? {
            daysOfWeek: daysOfWeek?.availableWeeklyOnDays,
            timezone: daysOfWeek?.startOfDayTimezone || timeOfDay?.timezone,
            startTime: timeOfDay?.availableDailyStartingAtTime,
            endTime: timeOfDay?.availableDailyEndingAtTime,
          }
        : undefined,
    minimumPurchase: minimumPurchaseConstraints,
  };
};

export type PreparedConstraints = ReturnType<typeof prepareConstraints>;

export type Offer = OfferFragment & {
  preparedConstraints: PreparedConstraints;
};

export const prepareOffer = (
  offer: OfferFragment,
  productGroups: ProductGroupFragment[] | undefined,
  audience?: AudienceFragment,
): Offer => {
  const preparedConstraints = prepareConstraints(offer?.constraints || [], productGroups, audience);

  return {
    ...offer,
    preparedConstraints,
  };
};

export const formatThresholdViewByCountType = ({
  threshold,
  countBy,
}: {
  threshold: number;
  countBy: Earning_Mechanism_Product_Counting_Attribute;
}): string => {
  const thresholdViewByCountType = {
    [Earning_Mechanism_Product_Counting_Attribute.Quantity]: `${threshold} ${pluralize(
      threshold,
      'item',
    )}`,
    [Earning_Mechanism_Product_Counting_Attribute.UnitsCount]: `${threshold} ${pluralize(
      threshold,
      'item',
    )} of`,
    [Earning_Mechanism_Product_Counting_Attribute.MoneyAmount]: `$${threshold}`,
  };
  return thresholdViewByCountType[countBy];
};

type PaymentInstrumentTitlesProps = {
  supportedPaymentTypes: PaymentType[];
  paymentInstrumentsList: string[];
};

export const getPaymentInstrumentTitles = ({
  supportedPaymentTypes,
  paymentInstrumentsList,
}: PaymentInstrumentTitlesProps): string[] => {
  return paymentInstrumentsList.map(
    (instrument): string =>
      supportedPaymentTypes.find(
        (supportedInstrument: PaymentType) => supportedInstrument.type === instrument,
      )?.title || instrument,
  );
};
